<div class="flex h-screen overflow-hidden bg-slate-50 font-sans" (click)="closeUserMenu()">

  <!-- ── Sidebar ──────────────────────────────────────────────────── -->
  <aside
    class="relative flex flex-shrink-0 flex-col border-r border-slate-200 bg-white transition-all duration-200 ease-in-out"
    [class.w-64]="!collapsed"
    [class.w-16]="collapsed"
  >
    <!-- Logo -->
    <div class="flex h-16 flex-shrink-0 items-center border-b border-slate-200 px-4">
      <div class="flex items-center gap-3 overflow-hidden">
        <div class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-violet-600">
          <lucide-icon name="zap" [size]="15" color="white" [strokeWidth]="2.5"></lucide-icon>
        </div>
        <span *ngIf="!collapsed" class="truncate text-sm font-semibold tracking-tight text-slate-900">
          KernalHQ
        </span>
      </div>
    </div>

    <!-- Nav -->
    <nav class="flex flex-1 flex-col gap-0.5 overflow-y-auto px-2 py-4">
      <a
        *ngFor="let item of navItems"
        [routerLink]="item.route"
        routerLinkActive="!bg-violet-50 !text-violet-700 font-medium"
        [routerLinkActiveOptions]="{ exact: item.exact }"
        class="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
        [class.justify-center]="collapsed"
        [title]="collapsed ? item.label : ''"
      >
        <lucide-icon [name]="item.icon" [size]="18" [strokeWidth]="1.75" class="flex-shrink-0"></lucide-icon>
        <span *ngIf="!collapsed" class="truncate">{{ item.label }}</span>
      </a>
    </nav>

    <!-- Footer -->
    <div class="flex-shrink-0 border-t border-slate-200 p-2">
      <!-- User row (expanded only) -->
      <div *ngIf="!collapsed" class="mb-1 flex items-center gap-3 rounded-lg px-2 py-2">
        <div class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-violet-100 text-xs font-semibold text-violet-700">
          {{ userInitials$ | async }}
        </div>
        <div class="min-w-0 flex-1">
          <p class="truncate text-xs font-medium text-slate-900">{{ displayName$ | async }}</p>
          <p class="truncate text-xs text-slate-400">{{ tierLabel$ | async }}</p>
        </div>
        <button
          (click)="signOut()"
          title="Sign out"
          class="flex-shrink-0 rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
        >
          <lucide-icon name="log-out" [size]="15" [strokeWidth]="1.75"></lucide-icon>
        </button>
      </div>

      <!-- Collapse toggle -->
      <button
        (click)="collapsed = !collapsed"
        class="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-xs text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
        [class.justify-center]="collapsed"
        [title]="collapsed ? 'Expand sidebar' : ''"
      >
        <lucide-icon
          [name]="collapsed ? 'chevron-right' : 'chevron-left'"
          [size]="15"
          [strokeWidth]="1.75"
          class="flex-shrink-0"
        ></lucide-icon>
        <span *ngIf="!collapsed">Collapse</span>
      </button>
    </div>
  </aside>

  <!-- ── Main area ─────────────────────────────────────────────────── -->
  <div class="flex min-w-0 flex-1 flex-col overflow-hidden">

    <!-- Impersonation banner -->
    <div *ngIf="isImpersonating$ | async" class="flex flex-shrink-0 items-center justify-between bg-amber-500 px-5 py-2">
      <div class="flex items-center gap-2 text-xs font-medium text-white">
        <lucide-icon name="triangle-alert" [size]="13" [strokeWidth]="2.5"></lucide-icon>
        <span>Impersonating</span>
        <span class="font-bold">{{ (impersonationContext$ | async)?.targetName }}</span>
        <span class="text-amber-200">·</span>
        <span class="text-amber-100">{{ (impersonationContext$ | async)?.targetEmail }}</span>
        <span class="ml-1 rounded bg-amber-400/60 px-1.5 py-0.5 text-[10px] font-semibold text-white">
          Admin: {{ (impersonationContext$ | async)?.actorAdminEmail }}
        </span>
        <span class="rounded bg-amber-400/60 px-1.5 py-0.5 text-[10px] font-semibold text-white">
          Expires {{ (impersonationContext$ | async)?.expiresAt | date:'shortTime' }}
        </span>
      </div>
      <button
        (click)="exitImpersonation()"
        [disabled]="exitingImpersonation"
        class="rounded bg-white/20 px-3 py-1 text-[11px] font-semibold text-white hover:bg-white/30 disabled:opacity-60"
      >
        {{ exitingImpersonation ? 'Exiting...' : 'Exit Impersonation' }}
      </button>
    </div>

    <!-- Topbar -->
    <header class="flex h-16 flex-shrink-0 items-center justify-between border-b border-slate-200 bg-white px-6">
      <!-- Breadcrumb -->
      <div class="flex items-center gap-2 text-sm">
        <span class="text-slate-400">Dashboard</span>
        <lucide-icon name="chevron-right" [size]="13" class="text-slate-300" [strokeWidth]="1.5"></lucide-icon>
        <span class="font-medium text-slate-900">{{ pageTitle }}</span>
      </div>

      <!-- Right controls -->
      <div class="flex items-center gap-2">

        <!-- Report a bug -->
        <button
          (click)="openBugReport($event)"
          title="Report a bug"
          class="flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-sm text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
        >
          <lucide-icon name="bug" [size]="17" [strokeWidth]="1.75"></lucide-icon>
          <span class="hidden lg:inline">Report a bug</span>
        </button>

        <!-- Notifications -->
        <div class="relative" (click)="$event.stopPropagation()">
          <button
            (click)="toggleNotifications($event)"
            class="relative flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100"
            [class.bg-slate-100]="notificationsOpen"
            [class.text-violet-600]="notificationsOpen"
          >
            <lucide-icon name="bell" [size]="18" [strokeWidth]="1.75"></lucide-icon>
            <span
              *ngIf="unreadCount > 0"
              class="absolute right-1.5 top-1.5 flex min-w-[16px] items-center justify-center rounded-full bg-violet-500 px-1 text-[10px] font-semibold leading-[16px] text-white"
            >{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
          </button>
          <app-notification-panel
            [isOpen]="notificationsOpen"
            (closed)="notificationsOpen = false"
          ></app-notification-panel>
        </div>

        <!-- User menu -->
        <div class="relative" (click)="$event.stopPropagation()">
          <button
            (click)="toggleUserMenu($event)"
            class="flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-slate-100"
          >
            <div class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-violet-100 text-xs font-semibold text-violet-700">
              {{ userInitials$ | async }}
            </div>
            <span class="hidden max-w-[120px] truncate text-sm font-medium text-slate-700 lg:block">
              {{ displayName$ | async }}
            </span>
            <lucide-icon name="chevron-down" [size]="14" class="text-slate-400" [strokeWidth]="1.75"></lucide-icon>
          </button>

          <!-- Dropdown -->
          <div
            *ngIf="userMenuOpen"
            class="absolute right-0 top-full z-50 mt-1.5 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg"
          >
            <!-- User info header -->
            <div class="border-b border-slate-100 px-4 py-3">
              <p class="truncate text-sm font-semibold text-slate-900">{{ displayName$ | async }}</p>
              <p class="mt-0.5 truncate text-xs text-slate-400">{{ (profile$ | async)?.email }}</p>
            </div>

            <!-- Nav items -->
            <div class="py-1">
              <a
                routerLink="/settings/account"
                (click)="closeUserMenu()"
                class="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50"
              >
                <lucide-icon name="user" [size]="15" [strokeWidth]="1.75" class="text-slate-400"></lucide-icon>
                Account Settings
              </a>
              <a
                routerLink="/settings/billing"
                (click)="closeUserMenu()"
                class="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50"
              >
                <lucide-icon name="credit-card" [size]="15" [strokeWidth]="1.75" class="text-slate-400"></lucide-icon>
                Billing
              </a>
              <button
                (click)="openBugReport($event)"
                class="flex w-full items-center gap-2.5 px-4 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50"
              >
                <lucide-icon name="bug" [size]="15" [strokeWidth]="1.75" class="text-slate-400"></lucide-icon>
                Report a bug
              </button>
            </div>

            <!-- Sign out -->
            <div class="border-t border-slate-100 py-1">
              <button
                (click)="signOut()"
                class="flex w-full items-center gap-2.5 px-4 py-2 text-sm text-red-600 transition-colors hover:bg-red-50"
              >
                <lucide-icon name="log-out" [size]="15" [strokeWidth]="1.75"></lucide-icon>
                Sign out
              </button>
            </div>
          </div>
        </div>

      </div>
    </header>

    <app-bug-report-dialog [open]="bugReportOpen" (closed)="bugReportOpen = false"></app-bug-report-dialog>

    <!-- Page content -->
    <main class="flex-1 overflow-y-auto">
      <router-outlet></router-outlet>
    </main>
  </div>

</div>

<!-- Global toast notifications -->
<app-toast-container></app-toast-container>

